import { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import { Loading } from "@medoma/components/Loading/Loading";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { formatAddress } from "@medoma/models/addresses";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import {
  getPatientFamilyName,
  getPatientGivenName,
  personalIdentityNumberTypeSchema,
  type IRegisteredPatient,
} from "@medoma/models/patients";
import {
  localizePhoneNumber,
  standardizePhoneNumber,
} from "@medoma/models/phoneNumbers";
import { conventionalPostalCodeSchema } from "@medoma/models/postalCode";
import Form from "@/components/Form/Form";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import {
  admitPatient,
  informationFieldConfigValueSchema,
  usePatientInformationFields,
  useTeams,
  type IAdmitPatient,
} from "@/api/Patients";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import AddressInput, { type AddressInputFields } from "./AddressInput";
import AdmissionDateInput, {
  type AdmissionDateInputFields,
} from "./AdmissionDateInput";
import AreaSelect, { type AreaSelectFields } from "./AreaSelect";
import CareSummaryInput, {
  type CareSummaryInputFields,
} from "./CareSummaryInput";
import CloseContactInput, {
  ExistingCloseContacts,
  type CloseContactInputFields,
} from "./CloseContactInput";
import DiagnosisCodeInput, {
  type DiagnosisCodeInputFields,
} from "./DiagnosisCodeInput";
import ExternalCareEpisodeId, {
  type ExternalCareEpisodeIdInputFields,
} from "./ExternalCareEpisodeIdInput";
import FlagsInput, { type FlagsInputFields } from "./FlagsInput";
import GeneralInformationInput, {
  type GeneralInformationInputFields,
} from "./GeneralInformationInput";
import HomeCareCheckbox, {
  type HomeCareCheckboxFields,
} from "./HomeCareCheckbox";
import PatientIdentifierInput, {
  type PatientIdentifierInputFields,
} from "./PatientIdentifierInput";
import PatientNameInput, {
  type PatientNameInputFields,
} from "./PatientNameInput";
import PatientTypeInput, {
  type PatientTypeInputFields,
} from "./PatientTypeInput";
import PetsInput, { type PetsInputFields } from "./PetsInput";
import PhoneNumberInput, {
  type PhoneNumberInputFields,
} from "./PhoneNumberInput";
import PrimaryResponsibleInput, {
  type PrimaryResponsibleInputFields,
} from "./PrimaryResponsibleInput";
import ReasonForAdmissionInput, {
  type ReasonForAdmissionInputFields,
} from "./ReasonForAdmissionInput";
import SafetyAlarmInput, {
  type SafetyAlarmInputFields,
} from "./SafetyAlarmInput";
import TeamSelect, { type TeamSelectFields } from "./TeamSelect";
import WardInput, { type WardInputFields } from "./WardInput";

type AdmitPatientFormFields = AddressInputFields &
  AdmissionDateInputFields &
  AreaSelectFields &
  CareSummaryInputFields &
  CloseContactInputFields &
  DiagnosisCodeInputFields &
  ExternalCareEpisodeIdInputFields &
  FlagsInputFields &
  GeneralInformationInputFields &
  HomeCareCheckboxFields &
  PatientIdentifierInputFields &
  PatientNameInputFields &
  PatientTypeInputFields &
  PetsInputFields &
  PhoneNumberInputFields &
  PrimaryResponsibleInputFields &
  ReasonForAdmissionInputFields &
  SafetyAlarmInputFields &
  TeamSelectFields &
  WardInputFields;

const PatientIdentity = () => (
  <>
    <PatientNameInput readonly />
    <PatientIdentifierInput readonly />
    <PatientTypeInput readonly />
  </>
);

export const AdmitPatientForm = ({
  patient,
}: {
  patient: IRegisteredPatient;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { countryCode } = useUserProfile();
  const queryClient = useQueryClient();

  const { address, relatives } = patient;
  const siteId = patient.site.id;

  const methods = useForm<AdmitPatientFormFields>({
    defaultValues: {
      admissionDate: machineFormat(new Date(), "yyyy-MM-dd"),
      givenName: getPatientGivenName(patient.name),
      familyName: getPatientFamilyName(patient.name),
      patientType: patient.patientType,
      identifier: patient.personalIdentityNumber,
      isTemporaryNumber:
        patient.personalIdentityNumberType ===
        personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber,
      addressLine1: address.addressLine1,
      postalCode: address.postalCode,
      city: address.city,
      coordinates: address.coordinates,
      additionalAddressInformation: address.additionalInformation ?? undefined,
      keyToPatient: address.keyToPatient ?? undefined,
      areaId: patient.area?.id.toString(),
      teamId: patient.team?.id.toString(),
      wardId: patient.ward.id.toString(),
      phoneNumber: patient.phoneNumber
        ? localizePhoneNumber({ countryCode, phoneNumber: patient.phoneNumber })
        : undefined,
      diagnosisCode: patient.diagnosis?.code,
      externalCareEpisodeId: patient.externalCareEpisodeId ?? undefined,
      flags: patient.flags ?? undefined,
      generalInformation: patient.information ?? undefined,
      homeCare: patient.homeCare ?? undefined,
      pets: patient.pet ?? undefined,
      careSummary: patient.careSummary ?? undefined,
      primaryResponsibleId: patient.primaryResponsible?.toString(),
      reasonForAdmission: patient.reasonForAdmission ?? undefined,
      safetyAlarmText: patient.safetyAlarmText ?? undefined,
    },
  });

  const { setError, reset, getValues } = methods;

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const { data: wardEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );

  const { data: teams } = useTeams(siteId);

  const { mutate, isPending: isPendingAdmitPatient } = useMutation({
    mutationFn: (admitPatientFormData: IAdmitPatient) =>
      admitPatient({ patientId: patient.id, admitPatientFormData }),
    onSuccess: async () => {
      await navigate("..", { relative: "path", replace: true });
      await queryClient.invalidateQueries();
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
  });

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  const otherInformationFields = [
    {
      condition:
        conditionalInformationFields.DiagnosisCode !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <DiagnosisCodeInput
          diagnosisCodeFieldConfig={conditionalInformationFields.DiagnosisCode}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.GeneralInformation !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <GeneralInformationInput
          generalInformationFieldConfig={
            conditionalInformationFields.GeneralInformation
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.Flags !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <FlagsInput flagsFieldConfig={conditionalInformationFields.Flags} />
      ),
    },
    {
      condition:
        conditionalInformationFields.Pet !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PetsInput petsFieldConfig={conditionalInformationFields.Pet} />
      ),
    },
    {
      condition:
        conditionalInformationFields.PersWristband !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <SafetyAlarmInput
          safetyAlarmFieldConfig={conditionalInformationFields.PersWristband}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.PrimaryResponsible !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PrimaryResponsibleInput
          primaryResponsibleFieldConfig={
            conditionalInformationFields.PrimaryResponsible
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.HomeCare !==
        informationFieldConfigValueSchema.enum.hidden,
      component: <HomeCareCheckbox />,
    },
    {
      condition:
        conditionalInformationFields.ReasonForAdmission !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <ReasonForAdmissionInput
          reasonForAdmissionFieldConfig={
            conditionalInformationFields.ReasonForAdmission
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.CareSummary !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <CareSummaryInput
          careSummaryFieldConfig={conditionalInformationFields.CareSummary}
        />
      ),
    },
  ];

  const hasAnyOtherInformation = otherInformationFields.some(
    (field) => field.condition,
  );

  const validateAndSubmit = methods.handleSubmit((data) => {
    const { closeContact } = data;
    const relative =
      closeContact?.name && closeContact.relation
        ? {
            name: closeContact.name,
            phone: closeContact.phoneNumber
              ? standardizePhoneNumber({
                  countryCode,
                  phoneNumber: closeContact.phoneNumber,
                })
              : null,
            isLivingTogether: closeContact.isLivingTogether,
            relation: closeContact.relation,
          }
        : null;

    const admitPatientFormData: IAdmitPatient = {
      admissionDate: calendarDateSchema.decode(data.admissionDate),
      address: formatAddress({
        addressLine1: data.addressLine1,
        postalCode: conventionalPostalCodeSchema.shape[countryCode].parse(
          data.postalCode,
        ),
        city: data.city,
        coordinates: data.coordinates,
        additionalInformation: data.additionalAddressInformation,
        keyToPatient: data.keyToPatient,
        countryCode,
      }),
      area: data.areaId ? Number(data.areaId) : null,
      team: data.teamId ? Number(data.teamId) : null,
      externalCareEpisodeId: data.externalCareEpisodeId,
      phone: standardizePhoneNumber({
        countryCode,
        phoneNumber: data.phoneNumber,
      }),
      ward: data.wardId ? Number(data.wardId) : null,
      careSummary: data.careSummary,
      diagnosisCode: data.diagnosisCode,
      flags: data.flags,
      homeCare: data.homeCare,
      information: data.generalInformation,
      pet: data.pets,
      primaryResponsible: data.primaryResponsibleId
        ? Number(data.primaryResponsibleId)
        : null,
      reasonForAdmission: data.reasonForAdmission,
      relative,
      safetyAlarmText: data.safetyAlarmText,
    };

    mutate(admitPatientFormData);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <AdmissionDateInput registeredAt={patient.registeredAt} />
        <PatientIdentity />
        {conditionalInformationFields.ExternalCareEpisodeId !==
        informationFieldConfigValueSchema.enum.hidden ? (
          <ExternalCareEpisodeId
            externalCareEpisodeIdFieldConfig={
              conditionalInformationFields.ExternalCareEpisodeId
            }
          />
        ) : null}
        {wardEnabled ? <WardInput siteId={siteId} /> : null}
        <PhoneNumberInput isRequired />
        <AddressInput
          siteId={siteId}
          additionalAddressInformationFieldConfig={
            conditionalInformationFields.AdditionalAdressInformation
          }
          keyToPatientFieldConfig={conditionalInformationFields.KeyToPatient}
        />
        {areaEnabled ? <AreaSelect siteId={siteId} /> : null}
        {teams && teams.length > 0 ? <TeamSelect siteId={siteId} /> : null}
        {hasAnyOtherInformation ? (
          <Fieldset legend={t`Övrig information`} visualStyle="framed">
            <Form.Section>
              {otherInformationFields
                .filter((field) => field.condition)
                .map((field, index) => (
                  <Fragment key={index}>{field.component}</Fragment>
                ))}
            </Form.Section>
          </Fieldset>
        ) : null}
        {conditionalInformationFields.CloseContact !==
        informationFieldConfigValueSchema.enum.hidden ? (
          relatives.length > 0 ? (
            <ExistingCloseContacts relatives={relatives} />
          ) : (
            <CloseContactInput
              closeContactFieldConfig={
                conditionalInformationFields.CloseContact
              }
            />
          )
        ) : null}

        {methods.formState.errors.root?.server?.message ? (
          <ErrorMessage
            message={methods.formState.errors.root.server.message}
          />
        ) : null}

        <FilledButton
          type="submit"
          width="fill-container"
          disabled={isPendingAdmitPatient}
        >
          {isPendingAdmitPatient ? <Spinner /> : null}
          <Trans>Fullfölj inskrivning</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};
